import React, { useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  filterBy,
  CompositeFilterDescriptor,
} from "@progress/kendo-data-query";
import {
  Grid as KendoGrid,
  GridColumn as Column,
  GridFilterChangeEvent,
  GridPageChangeEvent,
  GridCustomHeaderCellProps,
  GridCustomCellProps,
} from "@progress/kendo-react-grid";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import {
  HeaderThElement,
  PagerTargetEvent,
} from "@progress/kendo-react-data-tools";
import { Typography } from "@progress/kendo-react-common";
import { LoadingPanel } from "../../components/layout/Loading";
import { getAllNewsletter } from "./services/newsletter.services";
import { Button } from "@progress/kendo-react-buttons";
import moment from "moment";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import { checkAcessRights } from "../../_helper/helper";
import { EXCEL_EXPORT_ACCESS } from "../../_contstants/common";

const Newsletter: React.FC = () => {
  const isExcelAccess = checkAcessRights(
    location.pathname,
    EXCEL_EXPORT_ACCESS,
  );

  const handleExportExcel = () => {
    const datetime = moment().format("DD-MM-YYYY HH-mm");
    const table = document.querySelector(
      ".newsletter-excel-export",
    ) as HTMLTableElement;
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.table_to_sheet(table);

    const remarksColIndex = Array.from(table.querySelectorAll("th")).findIndex(
      (th) => th.textContent?.trim().toLowerCase() === "remarks",
    );

    if (remarksColIndex !== -1) {
      const range = XLSX.utils.decode_range(ws["!ref"] || "");
      for (let row = range.s.r + 1; row <= range.e.r; row++) {
        const cellAddress = XLSX.utils.encode_cell({
          r: row,
          c: remarksColIndex,
        });
        const cell = ws[cellAddress];
        if (
          cell &&
          typeof cell.v === "string" &&
          cell.v.includes("Show More")
        ) {
          cell.v = cell.v.replace(/Show More\s*$/i, "").trim();
        } else if (
          cell &&
          typeof cell.v === "string" &&
          cell.v.includes("Show Less")
        ) {
          cell.v = cell.v.replace(/Show Less\s*$/i, "").trim();
        }
      }
    }

    const colWidths: number[] = [];
    const rows = table.querySelectorAll("tr");

    rows.forEach((row) => {
      const cells = row.querySelectorAll("th, td");
      cells.forEach((cell, colIndex) => {
        const cellContent = cell.textContent || "";
        const cellLength = cellContent.length;

        colWidths[colIndex] = Math.max(
          colWidths[colIndex] || 10,
          cellLength + 2,
        );
      });
    });

    ws["!cols"] = colWidths.map((width) => ({ wch: width }));

    Object.keys(ws).forEach((key) => {
      if (!key.startsWith("!")) {
        const cell = ws[key];
        if (cell.z !== "m/d/yy") {
          if (typeof cell.v === "number") {
            const isDate = cell.t === "d";
            if (!isDate) {
              cell.t = "s";
              cell.v = `${cell.v}`;
            }
          }
        } else if (cell.z === "m/d/yy") {
          cell.z = "DD-MMM-YYYY";
        }
      }
    });

    XLSX.utils.book_append_sheet(wb, ws, `Newsletter Sheet 1`);

    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const data = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(data, `Kavish Newsletter List ${datetime}`);
  };

  return (
    <>
      <GridLayout cols={[{ width: "100%" }]}>
        <GridLayoutItem
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "4px 0 20px 0",
          }}
        >
          <Typography.h4 style={{ marginBottom: 0, marginLeft: "3px" }}>
            Newsletter List
          </Typography.h4>
          {isExcelAccess && (
            <Button
              style={{ marginRight: 5 }}
              type="button"
              fillMode={"solid"}
              themeColor={"primary"}
              onClick={handleExportExcel}
            >
              Excel Export
            </Button>
          )}
        </GridLayoutItem>
        <GridLayoutItem>
          <NewsletterGridComponent />
        </GridLayoutItem>
      </GridLayout>
    </>
  );
};

interface PageState {
  skip: number;
  take: number;
}

const initialDataState: PageState = { skip: 0, take: 10 };

const NewsletterGridComponent: React.FC = () => {
  const gridRef = useRef<any>(null);
  const dispatch = useAppDispatch();
  const NewsletterList = useAppSelector(
    (state) => state.newsletter.NewsletterList,
  );
  const loading = useAppSelector((state) => state.newsletter.loading);

  const [filter, setFilter] = React.useState<
    CompositeFilterDescriptor | undefined
  >(undefined);
  const [page, setPage] = React.useState<PageState>(initialDataState);
  const [pageSizeValue, setPageSizeValue] = React.useState<
    number | string | undefined
  >();

  const pageChange = (event: GridPageChangeEvent) => {
    const targetEvent = event.targetEvent as PagerTargetEvent;
    const take =
      targetEvent.value === "All" ? NewsletterList.length : event.page.take;

    if (targetEvent.value) {
      setPageSizeValue(targetEvent.value);
    }
    setPage({
      ...event.page,
      take,
    });
  };

  const handleFilterChange = (e: GridFilterChangeEvent) => {
    setFilter(e.filter);
    setPage({ skip: 0, take: page.take });
  };

  useEffect(() => {
    dispatch(getAllNewsletter());
  }, []);

  const HeaderCustomCell = (props: GridCustomHeaderCellProps) => (
    <HeaderThElement
      columnId={props.thProps?.columnId || ""}
      {...props.thProps}
      className={`table-header ${
        props.index === 3 || props.index === 4 ? "locked-header" : ""
      }`}
    >
      {props.children}
    </HeaderThElement>
  );

  const CustomCell = (props: any) => {
    return (
      <td {...props.tdProps} colSpan={1} className={"table-cell"}>
        {props.children}
      </td>
    );
  };

  const MyDataCustomCell = (props: GridCustomCellProps) => (
    <CustomCell {...props} />
  );

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <KendoGrid
        style={{ maxHeight: "calc(100vh - 152px)" }}
        filterable={true}
        className="table-bordered responsive-table newsletter-excel-export"
        filter={filter}
        data={
          filter
            ? filterBy(NewsletterList, filter).slice(
                page.skip,
                page.take + page.skip,
              )
            : NewsletterList.slice(page.skip, page.take + page.skip)
        }
        onFilterChange={handleFilterChange}
        total={NewsletterList.length}
        skip={page.skip}
        take={page.take}
        pageable={{
          buttonCount: 5,
          pageSizes: [10, 50, 100, 300, 500, "All"],
          pageSizeValue: pageSizeValue,
          type: "input",
        }}
        onPageChange={pageChange}
        cells={{
          headerCell: HeaderCustomCell,
          data: MyDataCustomCell,
        }}
      >
        <Column
          field="CreatedOn"
          title="Date"
          cells={{
            data: (props) => (
              <td>
                {props.dataItem.CreatedOn
                  ? moment(props.dataItem.CreatedOn, "DD/MM/YYYY").format(
                      "DD-MMM-YYYY",
                    )
                  : ""}
              </td>
            ),
          }}
          width={150}
        />
        <Column field="EmailID" title="Subscription On" />
      </KendoGrid>
    </>
  );
};

export default Newsletter;
